import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DatePipe, registerLocaleData } from '@angular/common';
import usLocale from '@angular/common/locales/es-US';
import deLocale from '@angular/common/locales/de';
import itLocale from '@angular/common/locales/it';

import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AuthModule, OidcConfigService } from 'angular-auth-oidc-client';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { ToastrModule, ToastContainerModule } from 'ngx-toastr';
import { MissingTranslationHandler, TranslateCompiler, TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { TagInputModule } from 'ngx-chips';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';

import { SharedModule } from './shared/shared.module';
import { LayoutContainersModule } from './containers/layout/layout.containers.module';
import { AppComponent } from './app.component';
import { environment } from 'src/environments/environment';

// import { FooterComponent } from './containers/layout/footer/footer.component';
// import { ColorSwitcherComponent } from './containers/layout/color-switcher/color-switcher.component';
// import { AngularFireModule } from '@angular/fire';

import { PORTAL_API_URL } from './api/portal-api.generated';
import { AUTH_API_URL } from './api/auth-api.generated';
import { configureAuth } from './auth/auth.service/auth.service';
import { CookieManagerService } from './shared/services/cookie-manager/cookie-manager.service';
import { UnauthorizedComponent } from './auth/unauthorized/unauthorized.component';
import { ForbiddenComponent } from './auth/forbidden/forbidden.component';
import { AuthInterceptor } from './auth/auth.interceptor/auth.interceptor';
import { AutoLoginComponent } from './auth/auto-login/auto-login.component';
import { SettingsComponent } from './settings/settings.component';
import { AppSettingsService } from './shared/services/app-settings/app-settings.service';
import { MissingTranslationHelper } from './shared/helpers/missing-translation-helper';
import { DateProxyPipe } from 'src/app/shared/pipes/date-proxy/date-proxy-pipe.pipe';
import { SettingsTabsComponent } from './settings/settings-tabs/settings-tabs.component';
import { NotificationsSettingsComponent } from './settings/notifications-settings/notifications-settings.component';
import { SettingsFooterComponent } from './settings/settings-footer/settings-footer.component';
import { GeneralSettingsComponent } from './settings/general-settings/general-settings.component';
import { ConfigurationService } from './shared/services/configuration/configuration.service';
import { AppRoutingModule } from './app-routing.module';
import { ProfileImagesModule } from './profiles/dashboard-shell/details/profile-images/profile-images.module';
import { DashboardShellComponent } from './profiles/dashboard-shell/dashboard-shell.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { MapperModule } from './shared/pipes/mapper/mapper.module';
import { SettingsClient, WALLET_API_URL } from './api/wallet-api.generated';
import { SWIPER_CONFIG, SwiperConfigInterface, SwiperModule } from 'ngx-swiper-wrapper';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { enGbLocale, deLocale as de_Locale, itLocale as it_Locale } from 'ngx-bootstrap/locale';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { HttpErrorInterceptor } from './shared/interceptors/http-error/http-error.interceptor';
import { NgxEchartsModule } from 'ngx-echarts';
import { SupportedLocalesCodes } from './shared/enums/enums';
import { ProfileNotificationsComponent } from './settings/notifications-settings/profile-notifications/profile-notifications.component';
import { ExpiredSessionComponent } from './shared/popups/expired-session/expired-session.component';
import { InvitationComponent } from './profiles/dashboard-shell/invitation-management/invitation/invitation.component';
import { ScePromotionComponent } from './shared/popups/sce-promotion/sce-promotion.component';
import { CustomTranslateCompiler } from './shared/helpers/translate-compiler/custom-translate-compiler';
import {HeaderComponent} from './containers/header/header.component';

registerLocaleData(usLocale, SupportedLocalesCodes.unitedStates);
registerLocaleData(deLocale, SupportedLocalesCodes.german);
registerLocaleData(itLocale, SupportedLocalesCodes.italian);

defineLocale(SupportedLocalesCodes.german, de_Locale);
defineLocale(SupportedLocalesCodes.britain, enGbLocale);
defineLocale(SupportedLocalesCodes.italian, it_Locale);

const DEFAULT_SWIPER_CONFIG: SwiperConfigInterface = {
  direction: 'horizontal',
  slidesPerView: 'auto',
  freeMode: false,
  mousewheel: false,
  keyboard: true,
  scrollbar: false,
  navigation: true,
  pagination: false,
  centeredSlides: false,
};

@NgModule({
  declarations: [
    AppComponent,
    AutoLoginComponent,
    SettingsComponent,
    //ColorSwitcherComponent,
    // SidebarComponent,
    //FooterComponent,
    UnauthorizedComponent,
    ForbiddenComponent,
    DateProxyPipe,
    SettingsTabsComponent,
    NotificationsSettingsComponent,
    SettingsFooterComponent,
    GeneralSettingsComponent,
    DashboardShellComponent,
    ProfileNotificationsComponent,
    ExpiredSessionComponent,
    InvitationComponent,
    ScePromotionComponent,
    HeaderComponent
  ],
  imports: [
    AuthModule.forRoot(),
    //AuthModule.forRoot(),
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    HttpClientModule,
    AppRoutingModule,
    PerfectScrollbarModule,
    LayoutContainersModule,
    BrowserAnimationsModule,
    SharedModule,
    TranslateModule.forRoot({
      compiler: { provide: TranslateCompiler, useClass: CustomTranslateCompiler },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MissingTranslationHelper },
      useDefaultLang: true,
    }),
    // AngularFireModule.initializeApp(environment.firebase)
    BsDropdownModule.forRoot(),
    BsDatepickerModule.forRoot(),
    ToastrModule.forRoot(),
    ToastContainerModule,
    InfiniteScrollModule,
    TagInputModule,
    AccordionModule,
    ProfileImagesModule,
    NgSelectModule,
    MapperModule,
    SwiperModule,
    AngularSvgIconModule.forRoot(),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
  ],
  providers: [
    {
      provide: PORTAL_API_URL,
      useValue: environment.baseApiUrl,
    },
    {
      provide: WALLET_API_URL,
      useValue: environment.baseApiUrl
        .replace('decr-portal-test', 'decr-portal')
        .replace('portal', 'wallet')
        .replace('localhost:5004', 'localhost:5002'),
    },
    {
      provide: AUTH_API_URL,
      useFactory: () => AppSettingsService.authServiceUrl,
    },
    SettingsClient,
    OidcConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: configureAuth,
      deps: [OidcConfigService, SettingsClient, ConfigurationService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    DatePipe,
    {
      provide: SWIPER_CONFIG,
      useValue: DEFAULT_SWIPER_CONFIG,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
